import { REPORT_DATASET_LABELS, type SavedReport } from '@/api/reports'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Card, DataTable, EmptyState, LoadingBlock, type Column } from '@/design'
import { useSession } from '@/lib/access'
import { formatDateKey } from '@/lib/filters'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { useDuplicateSavedReport, useReportsQuery, useScheduleSavedReport } from './hooks'
import './reports.css'

function columns(
  schedule: ReturnType<typeof useScheduleSavedReport>,
  duplicate: ReturnType<typeof useDuplicateSavedReport>,
  email: string | null,
  emailPending: boolean,
): Column<SavedReport>[] {
  return [
    { key: 'name', header: 'Report', sortValue: (row) => row.name, cell: (row) => row.name },
    { key: 'dataset', header: 'Dataset', cell: (row) => REPORT_DATASET_LABELS[row.dataset] },
    { key: 'owner', header: 'Owner', cell: (row) => row.owner },
    { key: 'updated', header: 'Updated', sortValue: (row) => row.updatedAt, cell: (row) => formatDateKey(row.updatedAt, true) },
    {
      key: 'schedule',
      header: 'Schedule',
      cell: (row) => (row.schedule ? `Weekly · ${row.schedule.recipients.join(', ')}` : 'Not scheduled'),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="rp-export-row">
          <button
            type="button"
            className="ci-button"
            data-variant="ghost"
            disabled={duplicate.isPending}
            onClick={() => duplicate.mutate(row.id)}
          >
            Duplicate
          </button>
          <button
            type="button"
            className="ci-button"
            data-variant="ghost"
            disabled={schedule.isPending || (!row.schedule && emailPending)}
            onClick={() =>
              schedule.mutate({
                id: row.id,
                schedule: row.schedule
                  ? null
                  : { cadence: 'weekly', recipients: email ? [email] : [], format: 'csv', nextRunAt: row.updatedAt },
              })
            }
          >
            {row.schedule ? 'Unschedule' : 'Schedule weekly'}
          </button>
        </div>
      ),
    },
  ]
}

/** Reports: every saved report, with run (open), duplicate and schedule actions. */
export function ReportsListPage() {
  const notLive = useNotLivePage('savedReports')
  const reports = useReportsQuery('saved', (client) => client.savedReports())
  const schedule = useScheduleSavedReport()
  const duplicate = useDuplicateSavedReport()
  const session = useSession()
  const search = useFilterSearch()

  const rows = reports.data?.data ?? []

  if (notLive) return <NotLivePage />

  return (
    <div className="ci-stack">
      <Card title="Reports" subtitle="Saved and scheduled reports over your demo data." busy={reports.isFetching}>
        {reports.isPending ? <LoadingBlock rows={6} /> : null}
        {reports.isError ? <EmptyState variant="outage" compact description="Reports could not be loaded." /> : null}
        {reports.data ? (
          <DataTable
            caption="Saved reports"
            columns={columns(schedule, duplicate, session.data?.email ?? null, session.isPending)}
            rows={rows}
            rowKey={(row) => row.id}
            rowHref={(row) => `/reports/${row.id}${search}`}
            initialSort={{ key: 'updated', direction: 'desc' }}
            empty={<EmptyState variant="no-data" compact title="No saved reports yet" />}
          />
        ) : null}
      </Card>
    </div>
  )
}
